// Utils
import { getElementsBoundingRect } from '../canvasUtils';
import { getZoomContentRect } from '../zoom/zoomScaleUtils';
import * as rectLib from '../../../common/maths/geometry/rect';
import { asObject } from '../../../common/utils/immutableHelper';
import { getScrollAsPoint } from '../../utils/dom/scrollableParentUtils';

// Selectors
import { getCanvasSize } from './canvasSizeSelector';
import getGridSize from '../../utils/grid/gridSizeSelector';
import { currentBoardCanvasElementsSelector } from '../../element/selectors/currentBoardSelector';
import {
    getCanvasElementMeasurementsBoundingRect,
    getMeasurementsMap,
} from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { canvasZoomScaleSelector, canvasZoomTranslationPxSelector } from './canvasZoomSelector';

/**
 * Retrieves the bounding rectangle of all the canvas elements, in pixels from the top left of the canvas document.
 *
 * NOTE: All coordinates should be positive. I.e. The coordinates are not translated based on the board's
 *  canvas origin.
 */
export const getCanvasElementsBoundingRectThunk =
    (canvasViewportNode, zoomScaleOverride, zoomTranslationOverride) =>
    (dispatch, getState, { measurementsStore }) => {
        const measurementsState = measurementsStore.getState();
        const measurements = getMeasurementsMap(measurementsState);

        const state = getState();
        const canvasElements = currentBoardCanvasElementsSelector(state);
        const gridSize = getGridSize(state);

        const zoomScale = zoomScaleOverride || canvasZoomScaleSelector(state);
        const zoomTranslation = zoomTranslationOverride || asObject(canvasZoomTranslationPxSelector(state));

        return getElementsBoundingRect(
            canvasElements,
            measurements,
            gridSize,
            getScrollAsPoint(canvasViewportNode),
            zoomScale,
            zoomTranslation,
        );
    };

/**
 * This determines the content rect to use for the zoom out operations.
 *
 * When zooming out we want to center the content, preserving its distance from the
 * top left.
 */
export const getCanvasZoomContentRectThunk =
    (canvasViewportClientRect) =>
    (dispatch, getState, { measurementsStore }) => {
        const state = getState();

        const canvasSize = asObject(getCanvasSize(state));

        // NOTE: For consistency we're using the measurement's store bounding rect calculation instead of the above
        //  getCanvasElementsBoundingRectThunk function, even though atm it's more accurate (because it adjusts
        //  the measurements of lines and boards).
        const measurementsState = measurementsStore.getState();
        const canvasElementsBoundingRect = getCanvasElementMeasurementsBoundingRect(measurementsState);

        return getZoomContentRect(canvasElementsBoundingRect, canvasSize, canvasViewportClientRect);
    };

/**
 * Finds the content rect to use when zooming to fit.
 * If the element bounding content rect is smaller than the viewport and we need to zoom into the
 * content, then use that.
 * Otherwise use same zoom content rect that we would when performing a manual zoom.
 */
export const getZoomToFitContentRectThunk =
    (canvasViewportNode, zoomScaleOverride, zoomTranslationOverride) => (dispatch, getState) => {
        const viewportRect = canvasViewportNode.getBoundingClientRect();

        const canvasElementsBoundingRect = dispatch(
            getCanvasElementsBoundingRectThunk(canvasViewportNode, zoomScaleOverride, zoomTranslationOverride),
        );

        const shouldZoomIntoContent =
            canvasElementsBoundingRect.width < viewportRect.width &&
            canvasElementsBoundingRect.height < viewportRect.height;

        if (shouldZoomIntoContent) {
            const padding = 40;
            return rectLib.addMargins(
                {
                    top: padding,
                    right: padding,
                    bottom: padding,
                    left: padding,
                },
                canvasElementsBoundingRect,
            );
        }

        return dispatch(getCanvasZoomContentRectThunk(viewportRect));
    };
